import type { InstanceScopedNamespacePath } from "building-types/components/instanceScopedNamespacePathTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type {
  GeometryRef,
  RoofBlockInstanceRef,
  VirtualRoofBlock,
  GableLabel,
  Roof,
  RoofSystemInternal,
  RoofFlagsTable,
  EvaluatedRoof,
  RoofBlocks,
} from "building-types/roofs/roofSystemTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { EvaluatedRoofSideOverridesTable, RoofSideOverridesTable } from "building-types/settings/roofTypes";
import { defaultRoofEdgeOverrides } from "building-types/settings/roofTypes";
import type { Table } from "common/data/table";
import { edgeIdsByPolyLine2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { resolveInstanceScopedEntityId } from "./entity/componentInstanceEntityResolve";
import { defaultRoofSideOverrides } from "./utils/roofTools";

export function isGeometryRef(v: any): v is GeometryRef {
  return typeof v === "object" && v.type === "GEOMETRY_REF";
}

export function isVirtualRoofBlock(v: any): v is VirtualRoofBlock {
  return typeof v === "object" && v.type === "VIRTUAL_ROOF_BLOCK";
}

/** Resolves a single block reference (string or instance-scoped path) to a plain entity ID. */
export function resolveBlockRefId(blockRef: ForeignEntityId<"BLOCK"> | RoofBlockInstanceRef): string {
  if (typeof blockRef === "string") {
    return blockRef;
  }
  return blockRef.entityId;
}

/**
 * Resolves a single block reference through the instance's vertical placement
 * (REPLICATION/REMAPPING). For a plain string ref this returns the string
 * unchanged. For a `RoofBlockInstanceRef` it computes the compound instance ID
 * (e.g. `"block-a__ci__instance-1"`) and checks whether that key exists in
 * `blockTable`; if not, falls back to the raw `entityId`.
 */
export function resolveBlockRefInstanceId<T>(
  blockRef: ForeignEntityId<"BLOCK"> | RoofBlockInstanceRef,
  blockTable: Table<T>,
): string {
  if (typeof blockRef === "string") {
    return blockRef;
  }
  return resolveInstanceScopedEntityId({
    path: blockRef,
    table: blockTable,
    fallbackId: blockRef.entityId,
  });
}

/** Extracts plain block IDs from a RoofBlocks value, returning an empty array for virtual blocks. */
export function roofBlockIds(blocks: RoofBlocks): string[] {
  return mapRoofBlocks(blocks, resolveBlockRefId);
}

/**
 * Returns all component instance IDs that a roof is nested inside of based on its block reference.
 * A virtual roof block references an instance-scoped LEVEL; non-virtual blocks may be
 * instance-scoped BLOCK refs. Plain (doc-native) block refs contribute no instances.
 */
export function roofBlocksComponentInstanceChain(blocks: RoofBlocks): readonly string[] {
  if (!blocks) {
    return [];
  }
  if (isVirtualRoofBlock(blocks)) {
    return blocks.levelUsage.componentInstanceChain ?? [];
  }
  const refs = Array.isArray(blocks) ? blocks : [blocks];
  const chain: string[] = [];
  for (const ref of refs) {
    if (typeof ref !== "string" && ref.componentInstanceChain) {
      chain.push(...ref.componentInstanceChain);
    }
  }
  return chain;
}

/**
 * Instance-aware variant of `roofBlockIds`. Resolves each block reference
 * through its `componentInstanceChain` so the returned IDs match the compound
 * keys used in building-generation tables (`blockPerimeters`, `blockLevelIds`,
 * etc.).
 */
export function resolveRoofBlockIds<T>(blocks: RoofBlocks, blockTable: Table<T>): string[] {
  return mapRoofBlocks(blocks, (ref) => resolveBlockRefInstanceId(ref, blockTable));
}

export function virtualRoofBlockNew(
  levelUsage: InstanceScopedNamespacePath<"LEVEL">,
  name: string,
  perimeter: PolyLine2,
): VirtualRoofBlock {
  return {
    type: "VIRTUAL_ROOF_BLOCK",
    levelUsage,
    name,
    perimeter,
    edgeIds: edgeIdsByPolyLine2(perimeter),
  };
}

export function evaluatedRoofNew(
  roofSystemId: string,
  id: string,
  roofVersion: number,
  sortIndex: number,
  name: string,
  blocks: string[] | VirtualRoofBlock,
  roofSettings: Partial<RoofSettings>,
  gableEndLabels: GableLabel[],
  roofSideOverrides: EvaluatedRoofSideOverridesTable = defaultRoofSideOverrides,
  flags?: RoofFlagsTable,
): EvaluatedRoof {
  return {
    id,
    entityType: "ROOF",
    parentId: roofSystemId,
    roofVersion,
    name,
    roofSettings,
    sortIndex,
    blocks,
    gableEndLabels,
    roofSideOverrides,
    roofEdgeOverrides: defaultRoofEdgeOverrides,
    flags,
    labels: {},
  };
}

export function roofNew(
  roofSystemId: string,
  id: string,
  roofVersion: number,
  sortIndex: number,
  name: string,
  blocks: string[] | VirtualRoofBlock,
  roofSettings: Partial<RoofSettings>,
  gableEndLabels: GableLabel[],
  roofSideOverrides: RoofSideOverridesTable = defaultRoofSideOverrides,
  flags?: RoofFlagsTable,
): Roof {
  return {
    id,
    entityType: "ROOF",
    parentId: roofSystemId,
    roofVersion,
    name,
    roofSettings,
    sortIndex,
    blocks,
    gableEndLabels,
    roofSideOverrides,
    roofEdgeOverrides: defaultRoofEdgeOverrides,
    flags,
    labels: {},
  };
}

export function roofSystemInternalNew(id: string, name: string, subRoofs: Table<EvaluatedRoof>): RoofSystemInternal {
  return {
    id,
    name,
    subRoofs,
  };
}

export function mapRoofBlocks<T>(
  blocks: RoofBlocks,
  mapRef: (blockRef: ForeignEntityId<"BLOCK"> | RoofBlockInstanceRef) => T,
): T[] {
  if (!blocks || isVirtualRoofBlock(blocks)) {
    return [];
  }
  if (typeof blocks === "string") {
    return [mapRef(blocks)];
  }
  if (Array.isArray(blocks)) {
    return blocks.map(mapRef);
  }
  return [mapRef(blocks)];
}
